import { BaseNodeData } from '@/types/node';
import type { Compositor, InputId, Layer, LayerId, Resource, ResourceId } from 'web';

/**
 * The compositor as the store holds it. Both `layers` and `resources` are Rust
 * maps, which the engine hands over as real JS `Map`s but which arrive as plain
 * objects once the data has been through JSON, so both are declared as records.
 */
export type CompositorData = Omit<Compositor, 'layers' | 'resources'> & {
  layers: Record<LayerId, Layer>;
  resources: Record<ResourceId, Resource>;
};

/**
 * Just the parts needed to answer what a layer shows. The table is optional
 * here, not because the store should ever be missing it, but because a document
 * saved before resources existed has none until the fold in `FlowGraph` puts one
 * there, and that fold is skipped for a node the graph does not support.
 */
type ResourceLookup = { resources?: Record<ResourceId, Resource> };

/**
 * The content used by a media layer or shape image fill. Text, solid shapes,
 * and groups hold what they show on the layer itself. A media layer whose resource is
 * missing is a corrupt document, and reads as undefined here.
 */
export function resolveLayerResource(compositor: ResourceLookup, layer: Layer): Resource | undefined {
  const { kind } = layer;
  if (kind.type === 'shape' && layer.fill?.type === 'image') {
    return compositor.resources?.[layer.fill.resource];
  }
  if (kind.type !== 'image' && kind.type !== 'video' && kind.type !== 'audio') {
    return undefined;
  }
  return compositor.resources?.[kind.resource];
}

export type CompositorNodeV3 = BaseNodeData & {
  data: CompositorData;
};

/**
 * Whether a layer is a video or audio, or holds one further down. A group counts
 * because a copy of it copies the clips inside it.
 *
 * Where the clip comes from does not matter. A copy of one fed by a connected
 * node is a new clip in the composition just as much as a copy of an uploaded
 * one, and both meet the same bug over a clip's time inside a group.
 */
function holdsAClip(compositor: CompositorData, layer: Layer): boolean {
  if (layer.kind.type === 'video' || layer.kind.type === 'audio') {
    return true;
  }
  return layer.children.some((childId) => {
    const child = compositor.layers[childId];
    return child !== undefined && holdsAClip(compositor, child);
  });
}

/**
 * Whether a layer can be duplicated: any layer that owns its content, plus media
 * fed by a connected node once `assetsPanelEnabled` is on. The copy stays in this
 * compositor and points at the same content, which is what dragging the same
 * tile out twice already gives.
 *
 * Duplicate needs no way back, so it is gated only to keep it with the panel that
 * makes a second layer on one piece of content an ordinary thing to have.
 */
export function canDuplicateLayer(
  compositor: CompositorData,
  layer: Layer,
  allowClips: boolean,
  assetsPanelEnabled: boolean,
): boolean {
  return isReusableLayer(compositor, layer, allowClips, assetsPanelEnabled);
}

/**
 * Whether a layer can be copied to the clipboard: any layer that owns its
 * content, and nothing fed by a connected node, media included.
 *
 * Media fed by a node is where this parts company with duplicate. The clipboard
 * has to carry the content with it, and content following a wire is only the
 * name of a handle on the compositor it came from. Pasting it back would add a
 * second entry for that wire, and pasting it elsewhere would name a wire that is
 * not there.
 */
export function canCopyLayer(compositor: CompositorData, layer: Layer, allowClips: boolean): boolean {
  return isReusableLayer(compositor, layer, allowClips, false);
}

/**
 * The shared rule behind duplicate and copy. A group qualifies only when it
 * holds something reusable, so a group with nothing in it greys the menu out.
 * Used to gate the menu items and count reuse for analytics; the engine is the
 * authority and skips whatever it can't clone.
 *
 * Text fed by a connected node is never reusable, either way. It holds its value
 * on the layer, so a copy would be a second layer claiming one wire.
 *
 * While `allowClips` is false, owned video/audio and any group containing one
 * are also excluded by the video/audio insertion rollout gate.
 */
function isReusableLayer(
  compositor: CompositorData,
  layer: Layer,
  allowClips: boolean,
  allowWiredMedia: boolean,
): boolean {
  if (isWiredText(layer)) {
    return false;
  }
  if (!allowWiredMedia && layerInputId(compositor, layer) !== undefined) {
    return false;
  }
  if (!allowClips && holdsAClip(compositor, layer)) {
    return false;
  }
  if (layer.kind.type === 'group') {
    return layer.children.some((childId) => {
      const child = compositor.layers[childId];
      return child !== undefined && isReusableLayer(compositor, child, allowClips, allowWiredMedia);
    });
  }
  return true;
}

/**
 * Removes selected descendants whose ancestor is also selected. Time offsets
 * already carry an ancestor's subtree, so editing both would apply a delta
 * twice.
 */
export function selectedLayerRoots(layers: Record<LayerId, Layer>, layerIds: LayerId[]): LayerId[] {
  const selected = new Set(layerIds);
  const parentById = new Map<LayerId, LayerId>();
  for (const [parentId, layer] of Object.entries(layers)) {
    for (const childId of layer.children) {
      parentById.set(childId, Number(parentId));
    }
  }

  return layerIds.filter((layerId) => {
    let parentId = parentById.get(layerId);
    while (parentId !== undefined) {
      if (selected.has(parentId)) {
        return false;
      }
      parentId = parentById.get(parentId);
    }
    return true;
  });
}

/**
 * Whether a layer can be deleted from the editor: anything that owns its
 * content, including a group, plus media fed by a connected node once
 * `assetsPanelEnabled` is on. The content stays in the compositor either way, so
 * the layer can be put back by dragging its tile out again.
 *
 * Media fed by a node waits on that flag because without the panel there is
 * nowhere to drag it back from, and the engine deliberately does not rebuild it,
 * which would make deleting one a door that only opens one way. The engine keeps
 * the same rule: `remove_layer` takes a `keep_wired_media` argument, and
 * `deleteLayers` passes the opposite of this flag into it, so a wired layer
 * nested inside a group is kept too.
 *
 * Text fed by a connected node can never be deleted. It keeps its value on the
 * layer instead of pointing at a resource, so nothing would remember that its
 * wire had already been placed and the next reconcile would build it again.
 *
 * Deleting a group takes what is inside it. Wired text is always kept, and wired
 * media is kept as well while the panel is off, both moved out to the top level
 * by the engine. Lock does not enter into it (lock guards moving and resizing,
 * not deleting). Shared by both delete entry points so the Delete key and the
 * menu agree.
 */
export function isDeletableLayer(compositor: ResourceLookup, layer: Layer, assetsPanelEnabled: boolean): boolean {
  if (isWiredText(layer)) {
    return false;
  }
  return assetsPanelEnabled || layerInputId(compositor, layer) === undefined;
}

/** Whether a layer is text fed by a connected node, the one kind delete always refuses. */
function isWiredText(layer: Layer): boolean {
  return layer.kind.type === 'text' && layer.kind.value.type === 'input';
}

/**
 * Whether a layer is a piece of added media (image / video / audio it owns
 * inline), the "content" counted in the added/deleted analytics.
 */
export function isOwnedMediaLayer(compositor: ResourceLookup, layer: Layer): boolean {
  return resolveLayerResource(compositor, layer)?.content.type === 'value';
}

/**
 * How many pieces of added media the given layers hold, looking inside groups
 * too, for the deleted-content count. Deleting a group takes its contents with
 * it, so counting only the layers the user selected would miss them. A layer
 * reached twice, when a group and one of its children are both selected, counts
 * once. The walk stops where the engine stops, which with `assetsPanelEnabled`
 * on is only a wired text layer. So it now goes through a wired media layer into
 * whatever is under it, where before it stopped there.
 */
export function countOwnedMedia(compositor: CompositorData, layerIds: LayerId[], assetsPanelEnabled: boolean): number {
  const counted = new Set<LayerId>();
  let count = 0;

  const visit = (layerId: LayerId) => {
    const layer = compositor.layers[layerId];
    if (layer === undefined || counted.has(layerId) || !isDeletableLayer(compositor, layer, assetsPanelEnabled)) {
      return;
    }
    counted.add(layerId);
    if (isOwnedMediaLayer(compositor, layer)) {
      count += 1;
    }
    layer.children.forEach(visit);
  };
  layerIds.forEach(visit);

  return count;
}

/**
 * How many layers show a given piece of content. Counted on demand rather than
 * kept on the resource, because every operation that adds, deletes, duplicates,
 * pastes, ungroups or migrates a layer would have to keep a stored count right,
 * in two languages, and a wrong one would show as a wrong number with nothing to
 * trace it back to. Zero is a normal answer: the compositor holds content no
 * layer shows.
 */
function layerShowsResource(layer: Layer, resourceId: ResourceId): boolean {
  const { kind } = layer;
  return (
    ((kind.type === 'image' || kind.type === 'video' || kind.type === 'audio') && kind.resource === resourceId) ||
    (kind.type === 'shape' && layer.fill?.type === 'image' && layer.fill.resource === resourceId)
  );
}

export function resourceUseCount(compositor: CompositorData, resourceId: ResourceId): number {
  return Object.values(compositor.layers).filter((layer) => layerShowsResource(layer, resourceId)).length;
}

/**
 * Layers that show this content, in key order. The assets hover card selects
 * them so "show on canvas" lands on the pictures already using the file.
 */
export function layerIdsForResource(compositor: CompositorData, resourceId: ResourceId): LayerId[] {
  return Object.entries(compositor.layers)
    .filter(([, layer]) => layerShowsResource(layer, resourceId))
    .map(([layerId]) => Number(layerId));
}

/**
 * The wire a layer follows. Text keeps its value on the layer, while the three
 * media kinds and a shape's image fill answer through the resource they point at, so this needs the
 * compositor and not just the layer. A version reading only the layer reports
 * every wired media layer as unwired, which would let it be deleted.
 */
export function layerInputId(compositor: ResourceLookup, layer: Layer): InputId | undefined {
  const type = layer.kind.type;
  switch (type) {
    case 'text':
      return layer.kind.value.type === 'input' ? layer.kind.value.data : undefined;
    case 'image':
    case 'video':
    case 'audio':
    case 'shape': {
      const content = resolveLayerResource(compositor, layer)?.content;
      return content?.type === 'input' ? content.data : undefined;
    }
    case 'group':
      return undefined;
    default: {
      const _exhaustiveCheck: never = type;
    }
  }
}
